import { useEffect, useState } from "react";
import { useUserAccess } from "@/contexts/UserAccessContext";
import { useAdminCheck } from "@/hooks/useAdminCheck";
import { useUserRole } from "@/hooks/useUserRole";
import { supabase } from "@/integrations/supabase/client";
import { CreditCard, CircleNotch as Loader2 } from "@phosphor-icons/react";
import { Button } from "@/components/ui/button";
import { Card, CardContent } from "@/components/ui/card";
import { useLocation } from "@/lib/router-compat";
import { trackInitiateCheckout } from "@/utils/metaPixel";

/**
 * Global guard that blocks ALL pages for logged-in users who haven't
 * registered a card via Stripe. Only public/auth pages are exempt.
 */
export function CardSetupGuard({ children }: { children: React.ReactNode }) {
  const { user, isLoading } = useUserAccess();
  const { isAdmin } = useAdminCheck();
  const { isBuyer, isDealer, isLoading: roleLoading } = useUserRole();
  const location = useLocation();
  const [needsCard, setNeedsCard] = useState(true); // Block by default until verified
  const [checking, setChecking] = useState(true);
  const [cardLoading, setCardLoading] = useState(false);

  // Pages that should NEVER be blocked (auth flow, public info, callbacks)
  const exemptPaths = [
    '/auth', '/auth/callback', '/success', '/pricing', '/activate-card',
    '/termeni', '/confidentialitate', '/gdpr', '/cookies',
    '/uk/auth', '/de/auth', '/uk/pricing', '/de/pricing',
    '/uk/terms', '/de/terms', '/uk/privacy', '/de/privacy',
    '/.lovable/oauth/consent',
  ];

  const isExempt = exemptPaths.some(p => location.pathname.startsWith(p));

  useEffect(() => {
    // Buyers don't need a card — they just browse
    if (
      isLoading ||
      roleLoading ||
      !user ||
      isExempt ||
      user.user_metadata?.account_type === 'private' ||
      (isBuyer && !isDealer)
    ) {
      setChecking(false);
      setNeedsCard(false);
      return;
    }

    const checkCard = async () => {
      setChecking(true);
      try {
        // Private sellers who paid for a one-off listing must be able to manage it
        // without being forced into the dealer subscription card setup flow.
        const { data: privateListings } = await supabase
          .from('listings_v2')
          .select('id')
          .eq('user_id', user.id)
          .eq('seller_type', 'private')
          .limit(1);

        if (privateListings && privateListings.length > 0) {
          setNeedsCard(false);
          return;
        }

        const { data: subscriber } = await supabase
          .rpc('get_my_subscription')
          .maybeSingle();

        const hasActiveSubscription = subscriber?.subscribed &&
          subscriber?.subscription_end &&
          new Date(subscriber.subscription_end) > new Date();

        const hasCard = !!subscriber?.stripe_customer_id;
        const cardRequired = subscriber?.card_required !== false;

        // Legacy dealers (card_required=false) or those with active sub + card pass through
        if (!cardRequired && hasActiveSubscription) {
          setNeedsCard(false);
        } else if (hasCard) {
          setNeedsCard(false);
        } else {
          setNeedsCard(true);
        }

      } catch (e) {
        console.error('[CardSetupGuard] Error checking subscription:', e);
        setNeedsCard(true); // Block on error — safety first
      } finally {
        setChecking(false);
      }
    };

    checkCard();
  }, [user, isLoading, isExempt, roleLoading, isBuyer, isDealer]);

  // Not logged in, exempt page, admin, or buyer (buyers don't need a card) — pass through
  if (!user || isExempt || isAdmin || isLoading || roleLoading || (isBuyer && !isDealer)) {
    return <>{children}</>;
  }

  // Show loading while checking card status (don't flash content)
  if (checking) {
    return (
      <div className="min-h-screen flex items-center justify-center bg-background">
        <Loader2 className="h-8 w-8 animate-spin text-muted-foreground" />
      </div>
    );
  }

  if (!needsCard) {
    return <>{children}</>;
  }

  // Block the entire page with card setup prompt
  return (
    <div className="min-h-screen flex items-center justify-center bg-background p-4">
      <div className="w-full max-w-md">
        <div className="text-center mb-8">
        <div className="mx-auto w-20 h-20 rounded-full bg-blue-100 dark:bg-blue-900/30 flex items-center justify-center mb-4">
            <CreditCard className="h-10 w-10 text-blue-600 dark:text-blue-400" />
          </div>
          <h1 className="text-2xl font-bold text-foreground mb-2">
            Ultimul pas! 🎉
          </h1>
          <p className="text-muted-foreground">
            Contul tău a fost creat cu succes! Mai ai un singur pas: înregistrează un card bancar și activează pachetul dealer ales (Start 29,99 €, Standard 50 € sau Large 120 € pe lună). Plata se face imediat prin Stripe, fără trial. Anulezi oricând.
          </p>
        </div>
        <Card>
          <CardContent className="pt-6">
            <Button
              className="w-full"
              size="lg"
              disabled={cardLoading}
              onClick={async () => {
                setCardLoading(true);
                try {
                  const { data: setupData, error: setupError } = await supabase.functions.invoke('setup-card');
                  if (!setupError && setupData?.url) {
                    trackInitiateCheckout();
                    window.location.href = setupData.url;
                    return;
                  }
                  console.error('[CardSetupGuard] setup-card failed:', setupError, setupData);
                } catch (e) {
                  console.error('[CardSetupGuard] setup-card error:', e);
                } finally {
                  setCardLoading(false);
                }
              }}
            >
              {cardLoading ? (
                <span className="flex items-center gap-2">
                  <Loader2 className="h-4 w-4 animate-spin" />
                  Se procesează...
                </span>
              ) : (
                <span className="flex items-center gap-2">
                  <CreditCard className="h-5 w-5" />
                  💳 Înregistrează cardul acum
                </span>
              )}
            </Button>
            <p className="text-xs text-muted-foreground text-center mt-3">
              Vei fi redirecționat către Stripe pentru a introduce datele cardului în siguranță.
            </p>
          </CardContent>
        </Card>

        <div className="text-center mt-6">
          <Button
            variant="ghost"
            size="sm"
            onClick={async () => {
              await supabase.auth.signOut();
              window.location.href = '/';
            }}
          >
            Deconectează-te
          </Button>
        </div>
      </div>
    </div>
  );
}
